import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Link from '@mui/material/Link'
import Popover, { PopoverProps } from '@mui/material/Popover'
import {
  DYNAMIC_FEEDBACK_UNLINKED_TASK_CODE,
  type IDynamicFeedbackResult,
  type IDynamicFeedbackTargets,
} from '@common/interfaces/dynamicEnvFeedback'
import api from '../../../api'
import { useIsTouch } from '../../../hooks/useIsMobile'
import Button from '../../Common/Button'
import Icon from '../../Common/Icon'
import Text from '../../Common/Text'
import TextField from '../../Common/TextField'
import RegionScreenshotPreview from '../../FeedbackWidget/components/RegionScreenshotPreview'
import RegionSelectOverlay from '../../FeedbackWidget/components/RegionSelectOverlay'
import useScreenshotCapture, {
  canCaptureRegion,
  isPageTooLargeToAutoCapture,
  type ISelectionRect,
} from '../../FeedbackWidget/hooks/useScreenshotCapture'
import { IPoint, markRegionScreenshot } from '../highlight'
import { loadTargets, readCachedTargets, reloadTargets } from '../targetsCache'
import styles from './FeedbackPopover.module.scss'
import TargetPicker from './TargetPicker'

interface IProps {
  point: IPoint
  // Started when the popover opened, so the page is captured as it was at the right-click.
  screenshot: Promise<string | null>
  onClose: () => void
}

interface ITargetsState {
  isLoading: boolean
  data: IDynamicFeedbackTargets | null
  error: string | null
}

const DATA_URL_PREFIX = /^data:image\/png;base64,/

// The screenshot filter skips anything marked as feedback UI, so the popover never captures itself.
const ROOT_PROPS = { 'data-feedback-widget': '' }

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Something went wrong. Please try again.'

const isUnlinkedTaskError = (error: unknown): boolean =>
  (error as { code?: unknown } | null)?.code === DYNAMIC_FEEDBACK_UNLINKED_TASK_CODE

const UNLINKED_TASK_MESSAGE =
  "That ticket isn't linked to this PR anymore, so the list has been refreshed. Check the ticket and submit again."

// An area that can't be marked is still worth sending as it was framed.
const markRegion = (
  dataUrl: string,
  rect: ISelectionRect | null,
  point: IPoint,
): Promise<string> =>
  rect
    ? markRegionScreenshot(dataUrl, rect, point).catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Screenshot: marking the click on the area failed', error)
        return dataUrl
      })
    : Promise.resolve(dataUrl)

const screenshotHint = (isAutoCaptureSkipped: boolean, canSelectArea: boolean): string => {
  if (!isAutoCaptureSkipped) return 'Your screen is attached, with a dot where you clicked.'
  return canSelectArea
    ? 'This page is too large to capture automatically. Select an area to attach one.'
    : 'This page is too large to capture, so no screenshot is attached.'
}

// Keeps the tester's pick when the fresh list still has it.
const keepOrDefault = (current: string | null, data: IDynamicFeedbackTargets): string | null =>
  current && data.targets.some((target) => target.id === current) ? current : data.defaultTargetId

const FeedbackPopover = ({ point, screenshot, onClose }: IProps) => {
  const [cachedTargets] = useState(readCachedTargets)
  const [targets, setTargets] = useState<ITargetsState>({
    isLoading: !cachedTargets,
    data: cachedTargets,
    error: null,
  })
  const [taskId, setTaskId] = useState<string | null>(cachedTargets?.defaultTargetId ?? null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [result, setResult] = useState<IDynamicFeedbackResult | null>(null)
  const isTouch = useIsTouch()
  const {
    isSelectingRegion,
    isCapturingRegion,
    dragRect,
    regionScreenshot,
    regionRect,
    hasRegionCaptureFailed,
    isGrabbingFrame,
    startSelectingRegion,
    clearRegionScreenshot,
    handleRegionMouseDown,
    handleRegionMouseMove,
    handleRegionMouseUp,
  } = useScreenshotCapture()
  // The region grab is a mouse drag, so touch screens keep the automatic capture only.
  const canSelectArea = useMemo(() => !isTouch && canCaptureRegion(), [isTouch])
  const isAutoCaptureSkipped = useMemo(isPageTooLargeToAutoCapture, [])

  useEffect(() => {
    let isCurrent = true
    loadTargets()
      .then((data) => {
        if (!isCurrent) return
        setTargets({ isLoading: false, data, error: null })
        setTaskId((current) => keepOrDefault(current, data))
      })
      .catch((error: unknown) => {
        // A cached list stays usable; Submit reports it if prod really is unreachable.
        if (isCurrent && !cachedTargets) {
          setTargets({ isLoading: false, data: null, error: errorMessage(error) })
        }
      })
    return () => {
      isCurrent = false
    }
  }, [cachedTargets])

  const hasDraft = Boolean(title.trim() || description.trim() || regionScreenshot)
  const canSubmit = Boolean(title.trim() && taskId) && !isSubmitting && !isCapturingRegion

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      if (!canSubmit || !taskId) return
      setIsSubmitting(true)
      setSubmitError(null)
      try {
        const dataUrl = regionScreenshot
          ? await markRegion(regionScreenshot, regionRect, point)
          : await screenshot
        setResult(
          await api.dynamicEnvFeedback.submit({
            taskId,
            title: title.trim(),
            description: description.trim() || undefined,
            pageUrl: window.location.href,
            screenshotBase64: dataUrl?.replace(DATA_URL_PREFIX, ''),
          }),
        )
      } catch (error) {
        if (!isUnlinkedTaskError(error)) {
          setSubmitError(errorMessage(error))
          return
        }
        setSubmitError(UNLINKED_TASK_MESSAGE)
        try {
          const data = await reloadTargets()
          setTargets({ isLoading: false, data, error: null })
          setTaskId((current) => keepOrDefault(current, data))
        } catch (reloadError) {
          setSubmitError(errorMessage(reloadError))
        }
      } finally {
        setIsSubmitting(false)
      }
    },
    [canSubmit, taskId, regionScreenshot, regionRect, point, screenshot, title, description],
  )

  // A stray click outside must not throw away typed feedback; Cancel and Esc still close.
  const handlePopoverClose = useCallback<NonNullable<PopoverProps['onClose']>>(
    (_event, reason) => {
      if (reason === 'backdropClick' && hasDraft && !result) return
      onClose()
    },
    [hasDraft, result, onClose],
  )

  const handleTitleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setTitle(event.target.value),
    [],
  )
  const handleDescriptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setDescription(event.target.value),
    [],
  )

  // A frame of the tab is being taken from its real pixels, so nothing of the popover may show.
  if (isGrabbingFrame) return null

  if (isSelectingRegion) {
    return (
      <RegionSelectOverlay
        dragRect={dragRect}
        onMouseDown={handleRegionMouseDown}
        onMouseMove={handleRegionMouseMove}
        onMouseUp={() => void handleRegionMouseUp()}
      />
    )
  }

  return (
    <Popover
      open
      anchorReference="anchorPosition"
      anchorPosition={{ top: point.y, left: point.x }}
      onClose={handlePopoverClose}
      slotProps={{ root: ROOT_PROPS, paper: { className: styles.paper } }}
    >
      {result ? (
        <div className={styles.body}>
          <Text variant="title">Feedback added</Text>
          <Text variant="bodySm" tone="secondary">
            {result.hasScreenshot
              ? "It's a subtask on the ticket now."
              : "It's a subtask on the ticket now, without a screenshot: none could be taken."}
          </Text>
          <Link
            href={result.subtaskUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            <Text variant="bodySm" component="span">
              View subtask
            </Text>
          </Link>
          <Text variant="bodySm" tone="tertiary">
            Right-click anywhere to add more.
          </Text>
          <div className={styles.footer}>
            <Button onClick={onClose}>Done</Button>
          </div>
        </div>
      ) : (
        <form className={styles.body} onSubmit={handleSubmit}>
          <Text variant="title">Add feedback</Text>
          <TargetPicker
            isLoading={targets.isLoading}
            error={targets.error}
            targets={targets.data}
            selectedTaskId={taskId}
            onSelect={setTaskId}
          />
          <TextField
            id="dynamic-feedback-title"
            name="title"
            label="Title"
            useFinalForm={false}
            size="small"
            fullWidth
            required
            autoFocus
            value={title}
            onChange={handleTitleChange}
          />
          <TextField
            id="dynamic-feedback-description"
            name="description"
            label="Description"
            useFinalForm={false}
            size="small"
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={handleDescriptionChange}
          />
          <RegionScreenshotPreview
            isCapturing={isCapturingRegion}
            hasFailed={hasRegionCaptureFailed}
            screenshot={regionScreenshot}
            failedMessage="Couldn't capture this area. Try again, or submit without it."
            onClear={clearRegionScreenshot}
          />
          {!regionScreenshot && !isCapturingRegion && (
            <div className={styles.screenshot}>
              <Text variant="caption" tone="tertiary" casing="role">
                {screenshotHint(isAutoCaptureSkipped, canSelectArea)}
              </Text>
              {canSelectArea && (
                <Button
                  secondary
                  small
                  type="button"
                  className={styles.selectArea}
                  startIcon={<Icon name="scan" size="sm" />}
                  onClick={() => void startSelectingRegion()}
                >
                  Select area
                </Button>
              )}
            </div>
          )}
          {submitError && (
            <Text variant="bodySm" tone="error" casing="none">
              {submitError}
            </Text>
          )}
          <div className={styles.footer}>
            <Button secondary type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit} isLoading={isSubmitting}>
              Submit
            </Button>
          </div>
        </form>
      )}
    </Popover>
  )
}

export default FeedbackPopover
